import React, { useState, useRef, useEffect } from 'react';
import { Song } from '../types';
import { Play, Pause, SkipForward, SkipBack, Disc, Volume2 } from 'lucide-react';

interface MusicPlayerProps {
  currentSong: Song | null;
  isPlaying: boolean;
  onPlayPause: () => void;
  onNext: () => void;
  onPrev: () => void;
  playlist: Song[];
  onSelectSong: (song: Song) => void;
}

const MusicPlayer: React.FC<MusicPlayerProps> = ({
  currentSong,
  isPlaying,
  onPlayPause,
  onNext,
  onPrev,
  playlist,
  onSelectSong
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isDragging, setIsDragging] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(e => console.log("Playback prevented:", e));
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentSong]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };

    const updateDuration = () => {
      setDuration(audio.duration || 0);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
    };
  }, [isDragging]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handleProgressMouseDown = () => {
    setIsDragging(true);
  };

  const handleProgressMouseUp = () => {
    setIsDragging(false);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  if (!currentSong && playlist.length === 0) {
    return (
      <div className="fixed bottom-6 left-0 right-0 mx-auto w-[90%] max-w-2xl z-40">
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-stone-800 rounded-full flex items-center justify-center">
            <span className="text-2xl">🎵</span>
          </div>
          <h3 className="text-lg font-serif text-stone-800 mb-2">暂无音乐</h3>
          <p className="text-stone-500 text-sm">管理员还没有上传音乐，敬请期待美妙旋律</p>
        </div>
      </div>
    );
  }

  if (!currentSong) return null;

  return (
    <div className="fixed bottom-3 sm:bottom-6 left-2 right-2 sm:left-0 sm:right-0 sm:mx-auto w-auto sm:w-[90%] max-w-2xl z-40">
      <div 
        className={`bg-stone-900/90 backdrop-blur-md text-stone-100 rounded-xl sm:rounded-2xl shadow-2xl transition-all duration-500 overflow-hidden border border-stone-700 ${isExpanded ? 'h-[380px] sm:h-[420px]' : 'h-20 sm:h-24'}`}
      >
        <audio 
          ref={audioRef} 
          src={currentSong.url} 
          onEnded={onNext}
        />

        {/* Compact / Header View */}
        <div className="p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2 sm:mb-3" onClick={() => setIsExpanded(!isExpanded)}>
            <div className="flex items-center space-x-3 sm:space-x-4 cursor-pointer flex-1 min-w-0">
              <div className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-stone-600 flex-shrink-0 ${isPlaying ? 'animate-[spin_4s_linear_infinite]' : ''}`}>
                 {currentSong.coverUrl ? (
                   <img src={currentSong.coverUrl} alt="cover" className="w-full h-full object-cover" />
                 ) : (
                   <div className="w-full h-full bg-stone-800 flex items-center justify-center">
                      <Disc size={16} className="sm:w-5 sm:h-5 text-stone-500"/>
                   </div>
                 )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-xs sm:text-sm font-medium font-serif tracking-wide truncate">{currentSong.title}</h3>
                <p className="text-xs text-stone-400 truncate">{currentSong.artist}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
               <button onClick={onPrev} className="text-stone-400 hover:text-white transition p-1">
                 <SkipBack size={16} className="sm:w-5 sm:h-5" />
               </button>
               <button 
                 onClick={onPlayPause} 
                 className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-800 hover:bg-emerald-700 flex items-center justify-center transition shadow-lg"
               >
                 {isPlaying ? <Pause size={16} className="sm:w-5 sm:h-5" fill="currentColor" /> : <Play size={16} className="sm:w-5 sm:h-5 ml-0.5" fill="currentColor" />}
               </button>
               <button onClick={onNext} className="text-stone-400 hover:text-white transition p-1">
                 <SkipForward size={16} className="sm:w-5 sm:h-5" />
               </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center space-x-2 sm:space-x-3 text-xs text-stone-400">
            <span className="w-8 sm:w-10 text-right text-xs">{formatTime(currentTime)}</span>
            <div className="flex-1 relative">
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleProgressChange}
                onMouseDown={handleProgressMouseDown}
                onMouseUp={handleProgressMouseUp}
                onTouchStart={handleProgressMouseDown}
                onTouchEnd={handleProgressMouseUp}
                className="w-full h-1 bg-stone-700 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #10b981 0%, #10b981 ${(currentTime / duration) * 100}%, #374151 ${(currentTime / duration) * 100}%, #374151 100%)`
                }}
              />
            </div>
            <span className="w-8 sm:w-10 text-xs">{formatTime(duration)}</span>
            
            {/* Volume Control - 移动端隐藏 */}
            <div className="hidden sm:flex items-center space-x-2 ml-4">
              <Volume2 size={14} className="text-stone-500" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="w-16 h-1 bg-stone-700 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #10b981 0%, #10b981 ${volume * 100}%, #374151 ${volume * 100}%, #374151 100%)`
                }}
              />
            </div>
          </div>
        </div>

        {/* Expanded Playlist View */}
        <div className={`px-3 sm:px-4 pb-3 sm:pb-4 h-72 sm:h-80 overflow-y-auto no-scrollbar transition-opacity duration-300 ${isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="border-t border-stone-700 my-2"></div>
          <h4 className="text-xs uppercase tracking-widest text-stone-500 mb-3 mt-4">播放列表</h4>
          <div className="space-y-1 sm:space-y-2">
            {playlist.map((song, index) => (
              <div 
                key={song.id} 
                onClick={() => onSelectSong(song)}
                className={`flex items-center p-2 rounded-lg cursor-pointer transition hover:bg-stone-800 active:bg-stone-750 ${currentSong.id === song.id ? 'bg-stone-800' : ''}`}
              >
                 <span className={`w-6 sm:w-8 text-center text-xs mr-2 sm:mr-3 ${currentSong.id === song.id ? 'text-emerald-500' : 'text-stone-600'}`}>
                    {currentSong.id === song.id && isPlaying ? (
                       <div className="flex space-x-0.5 justify-center items-end h-3">
                         <div className="w-0.5 bg-emerald-500 h-2 animate-pulse"></div>
                         <div className="w-0.5 bg-emerald-500 h-3 animate-pulse delay-75"></div>
                         <div className="w-0.5 bg-emerald-500 h-1 animate-pulse delay-150"></div>
                       </div>
                    ) : (
                      <span className="text-stone-500 text-xs">{(index + 1).toString().padStart(2, '0')}</span>
                    )}
                 </span>
                 <div className="w-6 h-6 sm:w-8 sm:h-8 rounded overflow-hidden mr-2 sm:mr-3 flex-shrink-0">
                   {song.coverUrl ? (
                     <img src={song.coverUrl} alt="cover" className="w-full h-full object-cover" />
                   ) : (
                     <div className="w-full h-full bg-stone-800 flex items-center justify-center">
                        <Disc size={10} className="sm:w-3 sm:h-3 text-stone-600"/>
                     </div>
                   )}
                 </div>
                 <div className="flex-1 min-w-0">
                   <p className={`text-xs sm:text-sm truncate ${currentSong.id === song.id ? 'text-emerald-400' : 'text-stone-300'}`}>{song.title}</p>
                   <p className="text-xs text-stone-500 truncate">{song.artist}</p>
                 </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicPlayer;